<template>
  <div class="AuditsHeader my-3">
    <v-row class="headerCaption" align-start justify="space-between">
      <v-col cols="12" md="6" class="pa-0 py-2 text-center text-md-left fill-height overflow-hidden">
        <span class="headline">{{$t("Audits.Header.audits", [orgName])}}</span>
      </v-col>
      <v-col cols="12" md="6" class="pa-0 py-2 text-center text-md-right no-wrap d-flex">
        <OrganizationFilter
          v-if="(userRoles && userRoles.isAdmin) || allowedOrganizations.length > 0"
          :allowedOrgs="allowedOrganizations"
          :selectedOrganization="selectedOrganization"
          :solo="solo"
          :outline="outline"
        />
        <v-select
          v-model="selectedDate"
          :items="optionsDates"
          item-text="label"
          item-value="id"
          solo
          height="40px"
          append-icon=""
          single-line
          flat
          dense
          class="DateSelector ma-0 ml-3"
          color="black"
          :loading="loadingQuery && '#0098E1'"
          @click="openDateSelector"
          hide-details
        >
          <template v-slot:append>
            <v-icon color="black">date_range</v-icon>
          </template>
          <template
          v-for="slotName in ['item', 'selection']"
          :slot="slotName"
          slot-scope="data"
          >
            <div slot="item" class="AuditDateItem">
              <v-icon v-if="data.item.haveNonPublishedAudits === true" small color="warning">priority_high</v-icon>
              <span>{{data.item.label}}</span>
            </div>
          </template>
        </v-select>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import OrganizationFilter from '../Dashboard/OrganizationFilter'
import { trackEvent  } from '../../lib/track-events'
import { EVENTS_TO_TRACK } from '../../constants/const'
import _some from 'lodash/some'
import { DateTime } from 'luxon'

const parseDateLabels = (deal) => {
  const formattedDate = DateTime.fromISO(deal.createdAt).toFormat('MMM yyyy')
  return `${deal.hubspotId} - ${formattedDate}`
}

export default {
  data () {
    return {
      selectedDate: 0,
      solo: true,
      outline: false,
      userRoles: this.$store.getters.userRoles,
      selectedOrganization: this.$store.getters.selectedOrganization.id
    }
  },
  components: {
    OrganizationFilter
  },
  props: {
    auditDealsDates: Array,
    orgInfo: Object,
    loadingQuery: Boolean,
    loadingDeals: {
      type: Boolean,
      default: false
    },
    preferredDealKey: {
      type: String,
      default: ''
    }
  },
  computed: {
    optionsDates () {
      if (!this.auditDealsDates.length) {
        return [{id: 0, label: this.$t('Audits.Header.noAudits')}]
      } else {
        return this.auditDealsDates.map((auditDeal) => {
          return {
            id: auditDeal.id,
            label: parseDateLabels(auditDeal),
            haveNonPublishedAudits: _some([...auditDeal.audits, ...auditDeal.flowMeasurements, ...auditDeal.pluviometricCharts], { isPublished: false }),
            createdAt: auditDeal.createdAt,
            hubspotId: auditDeal.hubspotId,
          }
        })
      }
    },
    orgName () {
      return this.$store.getters.selectedOrganization.name || (this.orgInfo && this.orgInfo.name)
    },
    allowedOrganizations () {
      if (this.userRoles && (this.userRoles.isAdmin || this.userRoles.farmhand)) {
        return []
      }

      const orgs = Object.keys(this.$store.getters.userPermissions)
      if (orgs.length > 0) {
        orgs.push(this.$store.getters.userOrganization.id)
      }
      return orgs
    }
  },
  methods: {
    findByDealKey (dates) {
      const wantedKey = String(this.preferredDealKey || '').trim()
      if (!wantedKey) return null
      return dates.find((e) => String(e.hubspotId || '').trim() === wantedKey) || null
    },
    findDate (dates) {
      return dates.find((e) => {
        return new Date(this.date) >= new Date(e.from_date) && new Date(this.date) <= new Date(e.to_date)
      })
    },
    isCampaignReport (date) {
      const CAMPAIGN_REPORT_MIN_DAYS = 45
      const diffTime = new Date(date.toDate).getTime() - new Date(date.fromDate).getTime()

      // If the report has a date difference greater than 45 days, it is considered a campaign report
      return (diffTime / 1000 / 60 / 60 / 24) > CAMPAIGN_REPORT_MIN_DAYS
    },
    openDateSelector () {
      trackEvent(EVENTS_TO_TRACK.CLICK_REPORTS_DATE_SELECTOR)
    }
  },
  watch: {
    auditDealsDates (newVal) {
      if (this.loadingDeals) {
        this.selectedDate = 0
        return
      }
      if (!newVal.length) {
        if (this.selectedDate !== 0) this.selectedDate = 0
        return
      }

      // Keep current selection if it still exists after list refresh
      const keepCurrent = newVal.some((item) => item.id === this.selectedDate)
      if (keepCurrent) return

      const selectedByDealKey = this.findByDealKey(newVal)
      if (selectedByDealKey) {
        if (this.selectedDate !== selectedByDealKey.id) {
          this.selectedDate = selectedByDealKey.id
        }
        return
      }

      // Get report date object that contains the requested date from the URL.
      const selectedDate = this.findDate(newVal)
      if (selectedDate) {
        if (this.selectedDate !== selectedDate.id) {
          this.selectedDate = selectedDate.id
        }
      } else {
        const fallbackId = newVal[0].id || 0
        if (this.selectedDate !== fallbackId) {
          this.selectedDate = fallbackId
        }
      }

      // If date was entered by url and was not found, show an alert
      if (!selectedDate && this.date) {
        this.$parent.alertURLAuditDealNotFound()
      }
    },
    selectedDate () {
      if (this.loadingDeals || !this.selectedDate) return
      trackEvent(EVENTS_TO_TRACK.OPEN_FULL_AUDIT_DEAL, { auditDealId: this.selectedDate })
      this.$emit('changeAuditDeal', this.selectedDate)
    },
    loadingDeals (isLoading) {
      if (isLoading) return
      const selectedByDealKey = this.findByDealKey(this.auditDealsDates || [])
      const targetId = selectedByDealKey
        ? selectedByDealKey.id
        : ((this.auditDealsDates && this.auditDealsDates[0] && this.auditDealsDates[0].id) || 0)
      if (targetId && this.selectedDate !== targetId) {
        this.selectedDate = targetId
      }
    }
  }
}
</script>


// WEBPACK FOOTER //
// Header.vue?37ec06b2